import React, { useState, useEffect } from "react";
import { LegalLinks } from "./LegalLinks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, Search, MoreHorizontal, Puzzle, Settings, LogOut, User } from "lucide-react";
import { T } from "../tokens";
import { NavItem } from "./NavItem";
import { ThemeToggle } from "./ThemeToggle";
import { ErrorBoundary } from "./ErrorBoundary";
import { initialsOf } from "../lib/text";
import { useLogout } from "../hooks/useAuth";
import { useMatchStats } from "../hooks/useMatches";
import { useCredits, usePlans } from "../hooks/usePayments";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useAuthStore } from "../store";

// ── Mobile breakpoint hook ────────────────────────────────────────────────
function useIsMobile(bp = 900) {
  const [mobile, setMobile] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

// ── Account menu row ──────────────────────────────────────────────────────
function MenuRow({ icon, label, danger, onClick }: {
  icon: React.ReactNode; label: string; danger?: boolean; onClick: () => void;
}) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button" role="menuitem" onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: "flex", alignItems: "center", gap: "10px", width: "100%",
        padding: "10px 14px", background: h ? T.s2 : "transparent", border: "none",
        cursor: "pointer", color: danger ? T.err : T.t1, fontFamily: T.body,
        fontSize: "13px", textAlign: "left", transition: "background 0.1s ease",
      }}
    >
      <span style={{ display: "flex", color: danger ? T.err : T.t2 }}>{icon}</span>
      {label}
    </button>
  );
}

// ── Header account menu (identity + session actions, top-right) ────────────
//   The conventional home for "who am I / sign out" — always reachable
//   regardless of how far the page has scrolled. Replaces the old sidebar
//   profile widget + bottom sign-out (which sat at the foot of a tall page).
function AccountMenu({ name, email, onProfile, onLogout }: {
  name: string; email: string; onProfile: () => void; onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(false);
  const initials = initialsOf(name);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
        aria-haspopup="menu" aria-expanded={open} aria-label="Account menu" title={name}
        style={{
          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
          background: T.tealBg, border: `1px solid ${open || h ? T.tealSoft : T.tealBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: T.mono, fontSize: "11px", fontWeight: 700, color: T.tealSoft,
          cursor: "pointer", transition: "border-color 0.12s ease",
        }}
      >
        {initials}
      </button>
      {open && (
        <>
          {/* click-away catcher */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div role="menu" style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 41,
            minWidth: "220px", background: T.s1, border: `1px solid ${T.b1}`,
            borderRadius: "10px", boxShadow: "0 8px 32px rgba(0,0,0,0.28)", overflow: "hidden",
          }}>
            <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.b0}` }}>
              <div style={{ fontFamily: T.body, fontSize: "13px", fontWeight: 600, color: T.t0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
              <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
            </div>
            <MenuRow icon={<User size={15}/>}   label="Profile"  onClick={() => { setOpen(false); onProfile(); }} />
            <div style={{ height: "1px", background: T.b0 }} />
            <MenuRow icon={<LogOut size={15}/>} label="Sign out" danger onClick={() => { setOpen(false); onLogout(); }} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Header bar ────────────────────────────────────────────────────────────
function HeaderBar({ isMobile, unseenCount, navigate, user, onLogout }: {
  isMobile:     boolean;
  unseenCount:  number;
  navigate:     (to: string) => void;
  user:         { name: string; email: string } | null;
  onLogout:     () => void;
}) {
  const [bellH, setBellH] = useState(false);
  const [q,     setQ]     = useState("");
  return (
    <div style={{
      flexShrink: 0, height: "52px",
      background: T.s1, borderBottom: `1px solid ${T.b0}`,
      display: "flex", alignItems: "center",
      padding: "0 clamp(16px, 3vw, 24px)", gap: "12px",
    }}>
      {!isMobile && (
        <div style={{
          flex: 1, maxWidth: "360px",
          display: "flex", alignItems: "center", gap: "8px",
          background: T.s2, border: `1px solid ${T.b1}`,
          borderRadius: "8px", padding: "0 12px", height: "34px",
        }}>
          <Search size={13} color={T.t3}/>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && q.trim()) {
                navigate(`/matches?q=${encodeURIComponent(q.trim())}`);
              }
            }}
            placeholder="Search matches…"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              fontFamily: T.body, fontSize: "13px", color: T.t1,
            }}
          />
        </div>
      )}

      <div style={{ flex: 1 }}/>

      <ThemeToggle />

      <button
        onMouseEnter={() => setBellH(true)}
        onMouseLeave={() => setBellH(false)}
        onClick={() => navigate("/matches")}
        style={{
          position: "relative",
          background: bellH ? T.s2 : "transparent",
          border: `1px solid ${bellH ? T.b1 : "transparent"}`,
          cursor: "pointer",
          width: 34, height: 34, borderRadius: "8px",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: bellH ? T.t0 : T.t2,
          transition: "all 0.12s ease",
        }}
      >
        <Bell size={16}/>
        {unseenCount > 0 && (
          <span style={{
            position: "absolute", top: "4px", right: "4px",
            background: T.teal, color: T.ctaTxt,
            borderRadius: "99px", fontSize: "8px",
            fontFamily: T.mono, fontWeight: 700,
            padding: "1px 4px", minWidth: "14px", textAlign: "center",
            lineHeight: 1.4, pointerEvents: "none",
          }}>
            {unseenCount > 99 ? "99+" : unseenCount}
          </span>
        )}
      </button>

      {/* Account menu — identity + sign out, always reachable (desktop). On mobile
          these live in the bottom nav / More sheet, so we don't duplicate them. */}
      {!isMobile && user && (
        <AccountMenu
          name={user.name} email={user.email}
          onProfile={() => navigate("/profile")}
          onLogout={onLogout}
        />
      )}
    </div>
  );
}

// ── Bottom tab bar (mobile only) ─────────────────────────────────────────
interface TabItem {
  icon:     React.ReactNode;
  label:    string;
  to?:      string;
  count?:   number;
  onClick?: () => void;
}

function BottomNav({ path, navigate, items }: {
  path:     string;
  navigate: (to: string) => void;
  items:    TabItem[];
}) {
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
      height: "56px",
      background: T.s1, borderTop: `1px solid ${T.b0}`,
      display: "flex", alignItems: "stretch",
    }}>
      {items.map((item, i) => {
        const active = item.to ? path === item.to : false;
        return (
          <button
            key={i}
            onClick={() => item.onClick ? item.onClick() : item.to && navigate(item.to)}
            style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "3px",
              background: active ? T.s2 : "transparent",
              border: "none", cursor: "pointer",
              color: active ? T.teal : T.t2,
              position: "relative",
              transition: "color 0.12s ease, background 0.12s ease",
            }}
          >
            <span style={{ fontSize: "17px", lineHeight: 1 }}>{item.icon}</span>
            <span style={{ fontFamily: T.body, fontSize: "9px", letterSpacing: "0.04em",
              fontWeight: active ? 600 : 400 }}>
              {item.label}
            </span>
            {item.count != null && item.count > 0 && (
              <span style={{
                position: "absolute", top: "7px", left: "calc(50% + 6px)",
                background: T.teal, color: T.ctaTxt,
                borderRadius: "99px", fontSize: "8px",
                fontFamily: T.mono, fontWeight: 700,
                padding: "1px 4px", minWidth: "14px", textAlign: "center",
                lineHeight: 1.4,
              }}>
                {item.count > 99 ? "99+" : item.count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ── More menu (mobile admin overlay) ─────────────────────────────────────
function MoreMenu({ navigate, onLogout, onClose }: {
  navigate:  (to: string) => void;
  onLogout:  () => void;
  onClose:   () => void;
}) {
  const items: { icon: React.ReactNode; label: string; to: string | null }[] = [
    { icon: <Puzzle  size={16}/>, label: "Extensions", to: "/extensions" },
    { icon: <Settings size={16}/>, label: "Admin",     to: "/admin"      },
    { icon: <LogOut  size={16}/>, label: "Sign out",   to: null          },
  ];
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 99, background: "rgba(0,0,0,0.4)" }}
      />
      <nav style={{
        position: "fixed", bottom: "56px", left: 0, right: 0, zIndex: 100,
        background: T.s1, borderTop: `1px solid ${T.b0}`,
        padding: "6px 0",
      }}>
        {items.map((item, i) => (
          <button
            key={i}
            onClick={() => {
              if (item.to) navigate(item.to);
              else onLogout();
              onClose();
            }}
            style={{
              display: "flex", alignItems: "center", gap: "16px",
              width: "100%", padding: "14px 24px",
              background: "transparent", border: "none", cursor: "pointer",
              color: item.to === null ? T.err : T.t1,
              fontFamily: T.body, fontSize: "14px", textAlign: "left",
            }}
          >
            <span style={{ fontSize: "16px", width: "20px", textAlign: "center" }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>
    </>
  );
}

// ── AppShell ─────────────────────────────────────────────────────────────
export function AppShell() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const logout     = useLogout();
  const isMobile   = useIsMobile();
  const { data: stats } = useMatchStats();
  const { data: credits } = useCredits();
  const { data: plans }   = usePlans();
  const paymentsOn = plans?.enabled ?? false;
  const token = useAuthStore(s => s.token);
  const user  = useAuthStore(s => s.user);
  const [moreOpen, setMoreOpen] = useState(false);

  const path = location.pathname;

  // Unseen match count — derived from localStorage watermark
  const [seenCount, setSeenCount] = useState<number>(() => {
    const raw = localStorage.getItem("calibre_seen_match_count");
    return raw ? (parseInt(raw, 10) || 0) : 0;
  });
  const unseenCount = Math.max(0, (stats?.totalMatches ?? 0) - seenCount);

  // When user opens /matches, mark all current matches as seen
  useEffect(() => {
    if (path === "/matches" && stats?.totalMatches != null) {
      localStorage.setItem("calibre_seen_match_count", String(stats.totalMatches));
      setSeenCount(stats.totalMatches);
    }
  }, [path, stats?.totalMatches]);

  // Close the more menu whenever the route changes
  useEffect(() => { setMoreOpen(false); }, [path]);

  const adminCheck = useQuery({
    queryKey: ["admin-access-shell", token ?? "anonymous"],
    queryFn:  async () => { await axios.get("/api/admin/metrics"); return true; },
    enabled:  !!token,
    retry:    false,
  });

  const isAdmin = adminCheck.isSuccess;

  const mobileTabItems: TabItem[] = [
    { icon: "⊞", label: "Home",    to: "/" },
    { icon: "✦", label: "Matches", to: "/matches", ...(stats?.totalMatches != null && { count: stats.totalMatches }) },
    { icon: "→", label: "Apply",   to: "/queue",   ...(stats?.autoApply    != null && { count: stats.autoApply    }) },
    { icon: "◎", label: "Profile", to: "/profile" },
    isAdmin
      ? { icon: <MoreHorizontal size={17}/>, label: "More",     onClick: () => setMoreOpen(o => !o) }
      : { icon: <LogOut         size={17}/>, label: "Sign out", onClick: () => logout.mutate() },
  ];

  return (
    // A fixed 100dvh frame: the shell itself never scrolls, so the sidebar stays
    // pinned and only <main> scrolls. (Previously minHeight let the whole document
    // grow on long pages, pushing the sidebar's sign-out to the very bottom.)
    <div style={{ display: "flex", height: "100dvh", overflow: "hidden", background: T.bg }}>

      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <nav style={{
          width: "220px", flexShrink: 0,
          background: T.s1, borderRight: `1px solid ${T.b0}`,
          display: "flex", flexDirection: "column",
          padding: "20px 12px",
        }}>
          <div style={{ marginBottom: "28px", paddingLeft: "12px" }}>
            <span style={{ fontFamily: T.display, fontSize: "16px", fontWeight: 700, color: T.t0 }}>
              Caliber <span style={{ color: T.teal }}>by DevAscend</span>
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1 }}>
            <NavItem icon="⊞" label="Dashboard"   active={path === "/"}           onClick={() => navigate("/")} />
            <NavItem icon="✦" label="Matches"      active={path === "/matches"}    onClick={() => navigate("/matches")} {...(stats?.totalMatches != null && { count: stats.totalMatches })} />
            <NavItem icon="→" label="Apply Queue"  active={path === "/queue"}      onClick={() => navigate("/queue")} {...(stats?.autoApply != null && { count: stats.autoApply })} />
            <NavItem icon="◎" label="Profile"      active={path === "/profile"}    onClick={() => navigate("/profile")} />
            {paymentsOn && <NavItem icon="◆" label="Credits" active={path === "/billing"} onClick={() => navigate("/billing")} {...(credits?.credits != null && { count: credits.credits })} />}
            {isAdmin && <NavItem icon="⌁" label="Extensions" active={path === "/extensions"} onClick={() => navigate("/extensions")} />}
            {isAdmin && <NavItem icon="⚙" label="Admin"      active={path === "/admin"}      onClick={() => navigate("/admin")} />}
          </div>
          {/* Identity + sign-out now live in the top-right account menu (always
              reachable), so the rail stays focused on navigation. */}
          {/* v1.1: Privacy · Terms · Help & feedback (renders nothing while those flags are off). */}
          <div style={{ paddingLeft: "12px" }}><LegalLinks align="left" /></div>
        </nav>
      )}

      {/* ── Right column: header + content ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
        <HeaderBar isMobile={isMobile} unseenCount={unseenCount} navigate={navigate} user={user} onLogout={() => logout.mutate()}/>
        <main style={{
          flex: 1, overflow: "auto",
          padding: isMobile ? "14px 12px 72px" : "28px 32px",
        }}>
          {/* Page-level boundary: a crash in one page shows a recoverable panel
              in the content area while the sidebar/nav stay usable — instead of
              blacking out the whole app. Keyed by route so navigating away clears it. */}
          <ErrorBoundary scope="page" key={path}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* ── Mobile bottom nav ── */}
      {isMobile && (
        <>
          <BottomNav path={path} navigate={navigate} items={mobileTabItems}/>
          {moreOpen && isAdmin && (
            <MoreMenu
              navigate={navigate}
              onLogout={() => logout.mutate()}
              onClose={() => setMoreOpen(false)}
            />
          )}
        </>
      )}

    </div>
  );
}
